import { hasUnitConversionPlaceholder, type SectionConfig } from "./types";

export interface ImportedTakeoffRow {
  [rowId: string]: string | number | null;
}

export interface ImportTakeoffResult {
  rows: ImportedTakeoffRow[];
  errors: string[];
  // Unit Conversion columns whose sheet header (e.g. "depth n.e. 656mm") was
  // found above the data — the number extracted from that header (656),
  // ready to store as-is on unitConversionValue (stored in mm; Configure
  // Headers divides by 1000 for display, matching its existing convention).
  unitConversionValues: { rowId: string; value: number }[];
}

// No shared "Excel column letter -> zero-based index" helper exists yet in
// src/utils/ (domain *ExcelUtils.ts files hardcode numeric indices instead).
function columnLetterToIndex(letters: string): number {
  let index = 0;
  for (let i = 0; i < letters.length; i++) {
    index = index * 26 + (letters.charCodeAt(i) - 64);
  }
  return index - 1;
}

function columnIndexToLetter(index: number): string {
  let n = index + 1;
  let letters = "";
  while (n > 0) {
    const remainder = (n - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}

interface ImportColumn {
  rowId: string;
  tableColumn: string;
  isDynamic: boolean;
  unitConversion: boolean;
  colIndex: number;
  // 0-based index into the raw sheet grid where this column's data begins.
  startRowIndex: number;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

interface HeaderMatch {
  text: string;
  // The number substituted into the "xxx" placeholder (e.g. 656 from
  // "depth n.e. 656mm") — only present for Unit Conversion matches.
  value?: number;
}

// Unit Conversion columns carry a "xxx"-style placeholder in tableColumn
// (e.g. "depth n.e. xxxmm") — the sheet's actual header has a real number
// substituted in (e.g. "depth n.e. 656mm"), so match that shape with a
// regex and capture the number. Dynamic columns have no placeholder; their
// sheet headers extend the base label with a per-instance suffix (e.g. base
// "Run" -> sheet header "Run 1"), so match on a case-insensitive substring
// instead.
function matchHeaderText(col: ImportColumn, headerText: string): HeaderMatch | null {
  const text = headerText.trim();
  if (col.unitConversion && hasUnitConversionPlaceholder(col.tableColumn)) {
    const pattern = escapeRegExp(col.tableColumn).replace(/x{2,}/i, "(\\d+)");
    const match = new RegExp(`^${pattern}$`, "i").exec(text);
    return match ? { text, value: parseInt(match[1], 10) } : null;
  }
  const needle = col.tableColumn.trim().toLowerCase();
  if (needle.length === 0 || !text.toLowerCase().includes(needle)) return null;
  return { text };
}

// Searches upward from directly above the data start row (headers sit above
// data, per the sheet convention) for the first cell in the same column
// whose text matches this column's configured name. Logs what it finds so
// the matching heuristic can be validated against real sheets.
function findHeaderMatch(raw: unknown[][], col: ImportColumn): HeaderMatch | null {
  const colLetter = columnIndexToLetter(col.colIndex);
  for (let row = col.startRowIndex - 1; row >= 0; row--) {
    const cell = raw[row]?.[col.colIndex];
    const text = cell === null || cell === undefined ? "" : String(cell).trim();
    if (!text) continue;
    const match = matchHeaderText(col, text);
    if (match) {
      console.log(
        `[Takeoff Import] Header for "${col.tableColumn}" (${colLetter}${col.startRowIndex + 1}): found "${match.text}" at ${colLetter}${row + 1}`,
      );
      return match;
    }
  }
  console.log(
    `[Takeoff Import] Header for "${col.tableColumn}" (${colLetter}${col.startRowIndex + 1}): NOT found searching ${colLetter}1:${colLetter}${col.startRowIndex}`,
  );
  return null;
}

// Takeoff Import Name is a cell reference like "A1" or "B2": the letters
// pick the column, the number picks the row that column's data starts on
// (so different columns can start at different rows in the same sheet).
// A bare letter with no row number defaults to starting at row 2, skipping
// an assumed single header row.
function parseCellRef(ref: string): { colIndex: number; startRowIndex: number } | null {
  const match = ref.trim().toUpperCase().match(/^([A-Z]+)(\d*)$/);
  if (!match) return null;
  const [, letters, rowDigits] = match;
  return {
    colIndex: columnLetterToIndex(letters),
    startRowIndex: rowDigits ? parseInt(rowDigits, 10) - 1 : 1,
  };
}

function collectImportColumns(config: SectionConfig): ImportColumn[] {
  const importColumns: ImportColumn[] = [];
  config.categories.forEach((category) => {
    category.subCategories.forEach((subCategory) => {
      subCategory.tableColumns.forEach((col) => {
        const ref = col.takeoffImportName?.trim();
        if (!ref) return;
        const parsed = parseCellRef(ref);
        if (parsed) {
          importColumns.push({
            rowId: col.rowId,
            tableColumn: col.tableColumn,
            isDynamic: col.isDynamic,
            unitConversion: col.unitConversion,
            colIndex: parsed.colIndex,
            startRowIndex: parsed.startRowIndex,
          });
        }
      });
    });
  });
  return importColumns;
}

// Mirrors the convention used by src/utils/foulSewersExcelUtils.ts and
// stormSewersExcelUtils.ts: workbook.Sheets[sheetName] lookup, then
// XLSX.utils.sheet_to_json(ws, { header: 1 }) for a raw unknown[][] grid.
export async function importTakeoffFromExcel(
  file: File,
  config: SectionConfig,
): Promise<ImportTakeoffResult> {
  if (!config.excelSheetName) {
    return { rows: [], errors: ["No Excel sheet name configured for this section."], unitConversionValues: [] };
  }

  const importColumns = collectImportColumns(config);
  if (importColumns.length === 0) {
    return {
      rows: [],
      errors: ["No columns have a Takeoff Import Name (Excel cell reference) configured."],
      unitConversionValues: [],
    };
  }

  const XLSX = await import("xlsx");
  const data = new Uint8Array(await file.arrayBuffer());
  const workbook = XLSX.read(data, { type: "array" });

  const sheetName = workbook.SheetNames.find(
    (name) => name.toLowerCase() === config.excelSheetName!.trim().toLowerCase(),
  );

  if (!sheetName) {
    return {
      rows: [],
      errors: [`Sheet "${config.excelSheetName}" was not found in the uploaded workbook.`],
      unitConversionValues: [],
    };
  }

  const sheet = workbook.Sheets[sheetName];
  const raw = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: null, raw: false });

  const unitConversionValues: { rowId: string; value: number }[] = [];
  importColumns
    .filter((col) => col.isDynamic || col.unitConversion)
    .forEach((col) => {
      const match = findHeaderMatch(raw, col);
      if (match?.value !== undefined) {
        unitConversionValues.push({ rowId: col.rowId, value: match.value });
      }
    });

  const minStartRow = Math.min(...importColumns.map((col) => col.startRowIndex));
  const maxDataRows = Math.max(0, raw.length - minStartRow);

  const rows: ImportedTakeoffRow[] = [];
  for (let offset = 0; offset < maxDataRows; offset++) {
    const mapped: ImportedTakeoffRow = {};
    let hasValue = false;
    importColumns.forEach(({ rowId, colIndex, startRowIndex }) => {
      const excelRow = raw[startRowIndex + offset] as unknown[] | undefined;
      const cell = excelRow?.[colIndex];
      const value = cell === undefined || cell === null || cell === "" ? null : (cell as string | number);
      if (value !== null) hasValue = true;
      mapped[rowId] = value;
    });
    if (hasValue) rows.push(mapped);
  }

  return { rows, errors: [], unitConversionValues };
}
